import { useMemo, useState } from "react";
import type { OrderSpec } from "@howcomet/shared";
import { CheckFrame } from "./CheckFrame";
import { seededShuffle } from "./shuffle";
import type { ShellProps } from "./registry";

/**
 * The spec gives the steps in the correct order and the client shuffles them —
 * that is the contract in the schema. The shuffle is seeded off the prompt so a
 * retry keeps the same arrangement: a child who is re-reading the options should
 * not have them move under them.
 */
function shuffled(steps: string[], seed: string): { text: string; index: number }[] {
  const items = seededShuffle(
    steps.map((text, index) => ({ text, index })),
    seed,
  );

  // A shuffle that lands back on the original order makes the check winnable by
  // tapping top to bottom. Nudge it if that happens. Unlike `pick`, where the
  // answer sitting first is simply one of the places it may sit, here the
  // identity arrangement is the answer.
  if (items.every((item, position) => item.index === position) && items.length > 1) {
    const first = items[0]!;
    items[0] = items[items.length - 1]!;
    items[items.length - 1] = first;
  }
  return items;
}

export function OrderShell({ spec, onDone, collecting }: ShellProps<OrderSpec>) {
  const options = useMemo(() => shuffled(spec.steps, spec.prompt), [spec]);
  const [sequence, setSequence] = useState<number[]>([]);
  const [misses, setMisses] = useState(0);

  const complete = sequence.length === spec.steps.length;
  const correct = complete && sequence.every((value, position) => value === position);
  const outcome = complete ? (correct ? "right" : "wrong") : null;

  return (
    <CheckFrame
      spec={spec}
      outcome={outcome}
      onRetry={() => {
        setMisses((m) => m + 1);
        setSequence([]);
      }}
      onCollect={() => onDone({ correct: true, firstTry: misses === 0 })}
      collecting={collecting}
    >
      <div className="flex flex-col gap-3">
        {options.map((option) => {
          const position = sequence.indexOf(option.index);
          const placed = position > -1;
          return (
            <button
              key={option.index}
              disabled={complete}
              onClick={() => {
                if (!placed) setSequence((s) => [...s, option.index]);
              }}
              className={`kidbtn flex items-center gap-3 rounded-2xl px-5 py-4 text-left text-[17px] font-bold ${
                placed ? "bg-lilac text-night" : "bg-panel text-cream"
              }`}
            >
              <span
                className={`disp flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  placed ? "bg-night text-gold" : "bg-cream/12 text-cream/50"
                }`}
              >
                {placed ? position + 1 : "?"}
              </span>
              {option.text}
            </button>
          );
        })}
      </div>

      {/* Undo the last tap without starting over. A five-year-old mis-taps
          constantly and clearing the whole thing each time is punishing. */}
      {sequence.length > 0 && !complete && (
        <button
          onClick={() => setSequence((s) => s.slice(0, -1))}
          className="kidbtn mt-4 rounded-full bg-cream/12 px-4 py-2 text-[15px] font-bold text-cream"
        >
          ↩ Undo
        </button>
      )}
    </CheckFrame>
  );
}
